import { Board } from '../engine/board';
import type { Level } from '../engine/level';
import { attachInput } from './input';
import { Renderer } from './renderer';
import { fitRoot, watchViewport } from './viewport';

export type PlayEvents = { onCleared(level: Level, moves: number): void; onBack(): void; onNext(level: Level): void; hasNext(level: Level): boolean; best(level: Level): number | undefined };

export class PlayScreen {
  private board: Board;
  private renderer: Renderer;
  private root: HTMLElement;
  private detach: () => void;
  private unwatch: () => void;
  private done = false;
  private raf = 0;

  constructor(host: HTMLElement, private level: Level, private ev: PlayEvents) {
    this.board = new Board(level);
    this.root = document.createElement('div');
    this.root.className = 'play';
    this.root.innerHTML = `
      <div class="hud top"><button class="icon back" aria-label="목록">‹</button><div class="title">level ${level.n}</div><button class="icon retry" aria-label="다시">↻</button></div>
      <canvas class="board"></canvas>
      <div class="hud bottom"><span class="moves"></span><span class="best"></span></div>
      <div class="overlay" hidden><div class="card"><div class="big">CLEAR</div><div class="sub"></div><div class="row"><button class="btn again">다시</button><button class="btn next">다음</button></div></div></div>`;
    host.replaceChildren(this.root);
    const canvas = this.root.querySelector<HTMLCanvasElement>('canvas')!;
    this.renderer = new Renderer(canvas);
    this.root.querySelector('.back')!.addEventListener('click', () => ev.onBack());
    this.root.querySelector('.retry')!.addEventListener('click', () => this.restart());
    this.root.querySelector('.again')!.addEventListener('click', () => this.restart());
    const next = this.root.querySelector<HTMLButtonElement>('.next')!;
    next.hidden = !ev.hasNext(level);
    next.addEventListener('click', () => ev.onNext(level));
    this.detach = attachInput(canvas, (x, y) => this.renderer.toCell(x, y), {
      onStart: ([x, y]) => { this.board.beginDrag(x, y); },
      onMove: ([x, y]) => { this.board.dragTo(x, y); },
      onEnd: () => { this.board.endDrag(); this.check(); },
    });
    this.unwatch = watchViewport(() => this.fit());
    const loop = () => { this.raf = requestAnimationFrame(loop); this.render(); };
    loop();
  }
  private fit() {
    const [w, h] = fitRoot(this.root);
    const top = 44, bottom = 36;
    this.renderer.canvas.style.top = `${top}px`;
    this.renderer.resize(w, Math.max(100, h - top - bottom), window.devicePixelRatio || 1);
  }
  private render() {
    this.renderer.draw(this.board);
    this.root.querySelector('.moves')!.textContent = `moves ${this.board.moves}`;
    const best = this.ev.best(this.level);
    this.root.querySelector('.best')!.textContent = best !== undefined ? `best ${best}` : '';
  }
  private check() {
    if (this.done || !this.board.cleared()) return;
    this.done = true;
    this.root.querySelector('.sub')!.textContent = `${this.board.moves} moves`;
    this.root.querySelector<HTMLElement>('.overlay')!.hidden = false;
    this.ev.onCleared(this.level, this.board.moves);
  }
  restart() { this.board = new Board(this.level); this.done = false; this.root.querySelector<HTMLElement>('.overlay')!.hidden = true; }
  destroy() { cancelAnimationFrame(this.raf); this.detach(); this.unwatch(); this.root.remove(); }
}
